import { Client, Conversation } from '@twilio/conversations';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getChatType,
  getSelectedChat,
  getToken,
  resetPage,
} from '../../store/apiSlice/chat/chatSlice';
import chatApi from '../../store/apiSlice/chat/chatApi';
import { ChatTypeEnum } from '../../enums/chat-enum';
import groupChatApi from '../../store/apiSlice/chat/groupApi';

const useTwilioClient = () => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const clientRef = useRef<Client | null>(null);
  const isInitializedRef = useRef(false);
  const dispatch = useDispatch();
  const { token } = useSelector(getToken);
  const selectedChat = useSelector(getSelectedChat);
  const chatId = selectedChat?.id;
  const externalId = selectedChat?.externalId;
  const chatType = useSelector(getChatType);

  useEffect(() => {
    if (!token || !externalId) {
      setConversation(null);
      return;
    }

    let cancelled = false;

    const syncClient = async () => {
      if (!clientRef.current) {
        const client = new Client(token);
        clientRef.current = client;
        isInitializedRef.current = false;
        setConversation(null);

        client.on('initialized', async () => {
          if (cancelled) return;
          isInitializedRef.current = true;
          try {
            const convo = await client.getConversationBySid(externalId);
            if (!cancelled) setConversation(convo);
          } catch (error) {
            console.log(error);
          }
        });

        return;
      }

      try {
        await clientRef.current.updateToken(token);
      } catch (error) {
        console.log(error);
      }

      if (!isInitializedRef.current) return;

      try {
        const convo = await clientRef.current.getConversationBySid(externalId);
        if (!cancelled) setConversation(convo);
      } catch (error) {
        console.log(error);
      }
    };

    syncClient();

    return () => {
      cancelled = true;
    };
  }, [token, externalId]);

  useEffect(() => {
    if (!conversation || !chatId) return;

    const onMessageAdded = () => {
      dispatch(resetPage());

      if (chatType === ChatTypeEnum.PERSONAL)
        dispatch(
          chatApi.util.invalidateTags([
            { type: 'messages', id: chatId },
            { type: 'chats', id: chatId },
          ])
        );
      else if (chatType === ChatTypeEnum.GROUP)
        dispatch(
          groupChatApi.util.invalidateTags([
            { type: 'messages', id: chatId },
            { type: 'chats', id: chatId },
          ])
        );
    };

    conversation.on('messageAdded', onMessageAdded);

    return () => {
      conversation.removeListener('messageAdded', onMessageAdded);
    };
  }, [conversation, dispatch, chatId, chatType]);
};

export default useTwilioClient;
